// CHG-033: public embed analytics tracker, one per mount session.
//
// Pure: every browser API arrives through `TrackerIo` (see browser.ts), so the
// tests run under `node --test` without a DOM. The wire shape is the backend
// CHG-003 contract at DIY-manuals-monorepo fc115bd (`validation.ts`): at most 5
// events and one shortcode per envelope, an exact key allowlist, any HTTP
// response final. Nothing here touches storage; the session id lives in memory
// and dies with the mount.

export type AnalyticsEventType = 'viewer_load' | 'anim_start' | 'step_anim_start' | 'anim_complete';
export type TrackerManualMode = 'assembly' | 'usage';

export interface AnalyticsEvent {
  v: 1;
  eventId: string;
  sessionId: string;
  eventType: AnalyticsEventType;
  shortcode: string;
  clientTs: string;
  manualMode: TrackerManualMode;
  stepIndex?: number;
}

/** Structural subset of core's `ViewerReadyInfo` (kept local so this module
 *  type-checks against any core version). */
export interface TrackerReadyInfo { manualMode: TrackerManualMode; totalSteps: number }
/** Structural subset of core's `StepAnimationLifecyclePayload`. */
export interface TrackerStepPayload { manualMode: TrackerManualMode; stepIndex: number; totalSteps: number }

export interface TrackerIo {
  /** keepalive fetch; resolves on any HTTP response, rejects on transport failure. */
  post(url: string, body: string): Promise<void>;
  /** navigator.sendBeacon; absent when the browser has none. */
  beacon?(url: string, body: string): boolean;
  now(): Date;
  randomId(): string;
  setTimeout(fn: () => void, ms: number): unknown;
  clearTimeout(handle: unknown): void;
  /** pagehide subscription; returns the unsubscribe function. */
  onUnload(fn: () => void): () => void;
}

export interface EmbedAnalyticsTracker {
  readonly sessionId: string;
  viewerLoad(info: TrackerReadyInfo): void;
  /** anim_start (once, assembly) + step_anim_start (each real clip start). */
  stepStarted(payload: TrackerStepPayload): void;
  /** anim_complete (once, assembly, last step). */
  stepFinished(payload: TrackerStepPayload): void;
  flush(final?: boolean): void;
  /** Final flush, then timers and listeners cleared; later calls are ignored. */
  dispose(): void;
}

const MAX_BATCH = 5;
const FLUSH_DELAY_MS = 1000;
const RETRY_DELAY_MS = 2000;
const STEP_INDEX_MAX = 9999;

/** Base URL → ingest URL, or null (analytics off) for anything not http(s). */
export function normalizeEndpoint(raw: unknown): string | null {
  if (typeof raw !== 'string') return null;
  const base = raw.trim().replace(/\/+$/, '');
  if (!/^https?:\/\/[^/\s]/i.test(base)) return null;
  return `${base}/events`;
}

const NOOP_TRACKER: EmbedAnalyticsTracker = {
  sessionId: '',
  viewerLoad() {},
  stepStarted() {},
  stepFinished() {},
  flush() {},
  dispose() {},
};

const normalizeMode = (mode: unknown): TrackerManualMode => (mode === 'usage' ? 'usage' : 'assembly');

export function createEmbedAnalyticsTracker(opts: {
  shortcode: string;
  endpoint: string | null;
  io: TrackerIo;
}): EmbedAnalyticsTracker {
  const { shortcode, endpoint: url, io } = opts;
  if (!url) return NOOP_TRACKER;

  const sessionId = io.randomId();
  const queue: AnalyticsEvent[] = [];
  let flushTimer: unknown = null;
  let inFlight = false;
  let flushWhenSettled = false;
  let loaded = false;
  let animStarted = false;
  let animCompleted = false;
  let disposed = false;
  let debugLogged = false;
  // Envelopes waiting for their single retry; a final flush sends them at once.
  const retries = new Set<{ body: string; timer: unknown; done: () => void }>();

  const debug = (err: unknown) => {
    if (debugLogged) return;
    debugLogged = true;
    try { console.debug('[Manuals3DViewer] analytics send failed', err); } catch { /* never surface */ }
  };

  const clearFlushTimer = () => {
    if (flushTimer !== null) io.clearTimeout(flushTimer);
    flushTimer = null;
  };

  const armFlushTimer = () => {
    if (flushTimer !== null) return;
    flushTimer = io.setTimeout(() => {
      flushTimer = null;
      flush();
    }, FLUSH_DELAY_MS);
  };

  /** One attempt; resolves true when the transport accepted the envelope. */
  const attempt = async (body: string, viaBeacon: boolean): Promise<boolean> => {
    if (viaBeacon && io.beacon) {
      try { if (io.beacon(url, body)) return true; } catch { /* fall back to fetch */ }
    }
    try {
      await io.post(url, body);
      return true;
    } catch (err) {
      debug(err);
      return false;
    }
  };

  /** Sends one envelope with at most one retry; resolves when that is over. */
  const send = async (body: string, viaBeacon: boolean): Promise<void> => {
    if (await attempt(body, viaBeacon)) return;
    if (disposed) return;
    await new Promise<void>((done) => {
      const entry = { body, timer: null as unknown, done };
      entry.timer = io.setTimeout(() => {
        retries.delete(entry);
        void attempt(body, viaBeacon).then(() => done());
      }, RETRY_DELAY_MS);
      retries.add(entry);
    });
  };

  const takeEnvelope = (): string => JSON.stringify({ events: queue.splice(0, MAX_BATCH) });

  const flush = (final = false) => {
    if (disposed && !final) return;
    clearFlushTimer();
    if (final) {
      // The page or the mount is going away: send everything now, the pending
      // retry first, without waiting for an in-flight envelope.
      for (const entry of [...retries]) {
        retries.delete(entry);
        io.clearTimeout(entry.timer);
        void attempt(entry.body, true).then(() => entry.done());
      }
      while (queue.length > 0) void send(takeEnvelope(), true);
      return;
    }
    if (queue.length === 0) return;
    if (inFlight) {
      flushWhenSettled = true;
      return;
    }
    inFlight = true;
    const body = takeEnvelope();
    // CHG033-CR01: events left behind a full batch are already due; they go out
    // as soon as this envelope settles instead of waiting for a new event.
    flushWhenSettled = queue.length > 0;
    void send(body, false).finally(() => {
      inFlight = false;
      if (disposed || queue.length === 0) return;
      if (flushWhenSettled || queue.length >= MAX_BATCH) {
        flushWhenSettled = false;
        flush();
      } else {
        armFlushTimer(); // a non-empty queue always has a deadline
      }
    });
  };

  const enqueue = (eventType: AnalyticsEventType, manualMode: TrackerManualMode, stepIndex?: number) => {
    if (disposed) return;
    const event: AnalyticsEvent = {
      v: 1,
      eventId: io.randomId(),
      sessionId,
      eventType,
      shortcode,
      clientTs: io.now().toISOString(),
      manualMode,
    };
    if (stepIndex !== undefined) event.stepIndex = stepIndex;
    queue.push(event);
    if (queue.length >= MAX_BATCH) flush();
    else armFlushTimer();
  };

  const unsubscribe = io.onUnload(() => flush(true));

  const guarded = <A extends unknown[]>(fn: (...args: A) => void) => (...args: A) => {
    try { fn(...args); } catch (err) { debug(err); }
  };

  return {
    sessionId,
    viewerLoad: guarded((info: TrackerReadyInfo) => {
      if (loaded || disposed) return;
      loaded = true;
      enqueue('viewer_load', normalizeMode(info?.manualMode));
    }),
    stepStarted: guarded((p: TrackerStepPayload) => {
      if (disposed) return;
      const mode = normalizeMode(p?.manualMode);
      if (mode === 'assembly' && !animStarted) {
        animStarted = true;
        enqueue('anim_start', mode);
      }
      const idx = p?.stepIndex;
      if (Number.isInteger(idx) && idx >= 0 && idx <= STEP_INDEX_MAX) enqueue('step_anim_start', mode, idx);
    }),
    stepFinished: guarded((p: TrackerStepPayload) => {
      if (disposed || animCompleted) return;
      if (normalizeMode(p?.manualMode) !== 'assembly') return;
      if (!Number.isInteger(p?.totalSteps) || p.totalSteps < 1 || p.stepIndex !== p.totalSteps - 1) return;
      animCompleted = true;
      enqueue('anim_complete', 'assembly');
    }),
    flush: guarded((final?: boolean) => flush(final === true)),
    dispose: guarded(() => {
      if (disposed) return;
      flush(true);
      disposed = true;
      clearFlushTimer();
      unsubscribe();
    }),
  };
}
